import type { CanonicalDocument } from '@besbello/design-core';

/**
 * HISTORIAL DE EDICION.
 *
 * El editor no tenia deshacer. Borrar una capa por error era irreversible, y en
 * una herramienta donde equivocarse cuesta empezar de cero, nadie prueba nada.
 *
 * Guarda DOCUMENTOS COMPLETOS, no diferencias. Un documento de diseno son unas
 * pocas capas: cabe de sobra en memoria, y comparar dos estados enteros es mucho
 * mas dificil de equivocar que aplicar y revertir parches. Cuando el documento
 * es la verdad, el historial tiene que ser de documentos.
 */

export interface Paso {
  documento: CanonicalDocument;
  /** Que se hizo, para poder decirlo en la interfaz: «deshacer mover capa». */
  descripcion: string;
  /**
   * Movimientos seguidos de la MISMA capa y del mismo tipo se funden en uno.
   * Sin esto, arrastrar una capa deja treinta pasos y deshacer no sirve de nada.
   */
  fusion: string | null;
  en: number;
}

/** Cuantos pasos se recuerdan. Cincuenta cubre una sesion de trabajo entera. */
export const LIMITE_PASOS = 50;

/** Dos cambios del mismo tipo separados por menos de esto se funden. */
const VENTANA_FUSION_MS = 600;

export interface Historial {
  pasados: Paso[];
  futuros: Paso[];
}

export function historialVacio(): Historial {
  return { pasados: [], futuros: [] };
}

/**
 * Registra un estado NUEVO.
 *
 * `fusion` es la clave de agrupacion: si el paso anterior tiene la misma y llegó
 * hace poco, se sustituye en vez de apilarse. Un arrastre manda `mover:<id>` en
 * cada cuadro y acaba siendo un solo paso deshacible.
 */
export function registrar(
  historial: Historial,
  documento: CanonicalDocument,
  descripcion: string,
  opciones: { fusion?: string | null; ahora?: number } = {},
): Historial {
  const ahora = opciones.ahora ?? Date.now();
  const fusion = opciones.fusion ?? null;
  const ultimo = historial.pasados[historial.pasados.length - 1];

  const seFunde =
    ultimo !== undefined &&
    fusion !== null &&
    ultimo.fusion === fusion &&
    ahora - ultimo.en < VENTANA_FUSION_MS;

  const paso: Paso = { documento, descripcion, fusion, en: ahora };

  /*
    Al fundir se conserva el documento del PRIMER paso del grupo.

    El historial guarda el estado ANTERIOR a cada cambio, que es al que hay que
    volver. Si al arrastrar se fuera sustituyendo por el estado mas reciente,
    deshacer devolveria al penultimo cuadro del arrastre en vez de a donde
    estaba la capa antes de tocarla, que es lo único que alguien espera.

    Y cualquier cambio nuevo invalida lo que se habia deshecho: esa rama se
    abandona. Conservarla llevaria a un arbol, y nadie espera un arbol al pulsar
    Ctrl+Z.
  */
  const pasados = seFunde
    ? [...historial.pasados.slice(0, -1), { ...ultimo, descripcion, en: ultimo.en }]
    : [...historial.pasados, paso];

  return {
    pasados: pasados.slice(-LIMITE_PASOS),
    futuros: [],
  };
}

export interface ResultadoNavegacion {
  historial: Historial;
  documento: CanonicalDocument | null;
  descripcion: string | null;
}

/**
 * Un paso atras.
 *
 * El estado ACTUAL se pasa aparte porque el historial guarda estados YA
 * confirmados: para poder rehacer hace falta saber de donde se venia.
 */
export function deshacer(historial: Historial, actual: CanonicalDocument): ResultadoNavegacion {
  const ultimo = historial.pasados[historial.pasados.length - 1];
  if (!ultimo) return { historial, documento: null, descripcion: null };

  return {
    historial: {
      pasados: historial.pasados.slice(0, -1),
      futuros: [{ ...ultimo, documento: actual }, ...historial.futuros].slice(0, LIMITE_PASOS),
    },
    documento: ultimo.documento,
    descripcion: ultimo.descripcion,
  };
}

export function rehacer(historial: Historial, actual: CanonicalDocument): ResultadoNavegacion {
  const siguiente = historial.futuros[0];
  if (!siguiente) return { historial, documento: null, descripcion: null };

  return {
    historial: {
      pasados: [...historial.pasados, { ...siguiente, documento: actual }].slice(-LIMITE_PASOS),
      futuros: historial.futuros.slice(1),
    },
    documento: siguiente.documento,
    descripcion: siguiente.descripcion,
  };
}

export const puedeDeshacer = (h: Historial): boolean => h.pasados.length > 0;
export const puedeRehacer = (h: Historial): boolean => h.futuros.length > 0;
